import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser, SignedIn } from '@clerk/clerk-react';
import {
  Send, ArrowLeft, Rocket, X, Image as ImageIcon,
  Target, TrendingUp, AlertTriangle, Loader2, Search, Shield,
  History, Plus, Trash2, MessageSquare
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ScannerContext } from '../types';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  image?: string;
  timestamp: Date;
}

interface ConversationSummary {
  id: number;
  title: string;
  ticker: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
}

const MoonshotPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const scannerContext = (location.state as { scannerContext?: ScannerContext })?.scannerContext || null;

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isExtractingPositions, setIsExtractingPositions] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation history list
  const loadConversations = async () => {
    if (!user?.id) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`/api/conversations?action=list&clerkId=${user.id}`);
      const data = await res.json();
      setConversations(data.conversations || []);
    } catch (e) {
      console.error('Failed to load conversations:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load a specific conversation
  const loadConversation = async (convoId: number) => {
    if (!user?.id) return;
    try {
      const res = await fetch(`/api/conversations?action=get&id=${convoId}&clerkId=${user.id}`);
      const data = await res.json();
      if (data.messages) {
        setMessages(data.messages.map((m: any) => ({
          id: String(m.id),
          role: m.role,
          content: m.content,
          timestamp: new Date(m.created_at)
        })));
        setConversationId(convoId);
        setShowHistory(false);
      }
    } catch (e) {
      console.error('Failed to load conversation:', e);
    }
  };

  // Delete a conversation
  const deleteConversation = async (convoId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.id) return;
    try {
      await fetch(`/api/conversations?id=${convoId}&clerkId=${user.id}`, { method: 'DELETE' });
      setConversations(prev => prev.filter(c => c.id !== convoId));
      if (conversationId === convoId) {
        startNewChat();
      }
    } catch (e) {
      console.error('Failed to delete conversation:', e);
    }
  };

  // Save messages to database
  const saveMessages = async (newMessages: { role: string; content: string }[], ticker?: string) => {
    if (!user?.id) return;
    try {
      const res = await fetch('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
          conversationId,
          ticker: ticker || scannerContext?.ticker,
          title: !conversationId ? (scannerContext?.ticker ? `${scannerContext.ticker} Analysis` : newMessages[0]?.content?.slice(0, 60) || 'New Chat') : undefined,
          messages: newMessages
        })
      });
      const data = await res.json();
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
      }
    } catch (e) {
      console.error('Failed to save conversation:', e);
    }
  };

  // Start a new chat
  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
  };

  // Format scanner context into a structured message for the AI
  const formatScannerContextMessage = (ctx: ScannerContext, userPrompt: string): string => {
    const legsDescription = ctx.legs.map(leg =>
      `${leg.action} ${leg.amount}x ${ctx.ticker} $${leg.strike} ${leg.type} (${leg.expiry}, ${leg.dte} DTE)`
    ).join('\n  ');

    // Build step scores breakdown if available
    let stepBreakdown = '';
    if (ctx.stepScores) {
      const s = ctx.stepScores;
      stepBreakdown = `
- 7-Step Breakdown:
  Step 1 Sentiment: ${s.step1_sentiment.score}/${s.step1_sentiment.max} (${s.step1_sentiment.passed ? 'PASS' : 'FAIL'})
  Step 2 Technicals: ${s.step2_technicals.score}/${s.step2_technicals.max} (${s.step2_technicals.passed ? 'PASS' : 'FAIL'})
  Step 3 Options Surface: ${s.step3_options.score}/${s.step3_options.max} (${s.step3_options.passed ? 'PASS' : 'FAIL'})
  Step 4 Risk: ${s.step4_risk.score}/${s.step4_risk.max} (${s.step4_risk.passed ? 'PASS' : 'FAIL'})
  Step 5 Greeks: ${s.step5_greeks.score}/${s.step5_greeks.max} (${s.step5_greeks.passed ? 'PASS' : 'FAIL'})
  Step 6 Entry: ${s.step6_entry.score}/${s.step6_entry.max} (${s.step6_entry.passed ? 'PASS' : 'FAIL'})
  Step 7 Review: ${s.step7_review.score}/${s.step7_review.max} (${s.step7_review.passed ? 'PASS' : 'FAIL'})
  Step 8 Sizing: ${s.step8_sizing.score}/${s.step8_sizing.max} (${s.step8_sizing.passed ? 'PASS' : 'FAIL'})`;
    }

    // Build quantitative metrics section if available
    let quantSection = '';
    if (ctx.volEdge !== undefined) {
      quantSection = `
- Quantitative Analysis:
  IV vs RV Edge: ${(ctx.volEdge! * 100).toFixed(1)}% (${ctx.volEdgeSignal}, ${ctx.volEdgeStrength})
  20d Realized Vol: ${ctx.rv20d !== undefined ? (ctx.rv20d * 100).toFixed(1) + '%' : 'N/A'}
  EWMA Forecast: ${ctx.ewmaForecast !== undefined ? (ctx.ewmaForecast * 100).toFixed(1) + '%' : 'N/A'}
  Market Regime: ${ctx.regime || 'N/A'}${ctx.regimeConfidence !== undefined ? ` (${(ctx.regimeConfidence * 100).toFixed(0)}% confidence)` : ''}
  Z-Score: ${ctx.zScore !== undefined ? ctx.zScore.toFixed(2) : 'N/A'}
  Kelly Contracts: ${ctx.kellyContracts !== undefined ? ctx.kellyContracts : 'N/A'}`;
    }

    return `[SCANNER PRE-ANALYZED TRADE — Trust this quantitative data as the foundation]

${userPrompt}

7-Step Analysis for ${ctx.ticker} (currently $${ctx.currentPrice.toFixed(2)}):
- Overall Score: ${ctx.score.toFixed(0)}/100 (${ctx.status})
- Status Reason: ${ctx.statusReason}
- Direction: ${ctx.direction}${stepBreakdown}
- Recommended Strategy: ${ctx.strategyName} (${ctx.creditDebit})
- Trade Legs:
  ${legsDescription}
- Max Profit: $${ctx.maxProfit.toFixed(0)} | Max Loss: $${ctx.maxLoss.toFixed(0)}
- Win Probability: ${(ctx.probabilityOfProfit * 100).toFixed(0)}%
- Risk/Reward: 1:${ctx.riskRewardRatio.toFixed(1)}
- Breakeven: $${Array.isArray(ctx.breakeven) ? ctx.breakeven.map(b => b.toFixed(2)).join(' / $') : ctx.breakeven.toFixed(2)}
- IV Rank: ${ctx.ivRank}${quantSection}
${ctx.warnings.length > 0 ? `- Warnings: ${ctx.warnings.join(', ')}` : ''}
${ctx.riskFlags.length > 0 ? `- Risk Flags: ${ctx.riskFlags.join(', ')}` : ''}
${ctx.analyzedAt ? `- Analyzed At: ${ctx.analyzedAt}` : ''}`;
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: string[] = [];
    const maxImages = 5;
    const currentCount = uploadedImages.length;
    const availableSlots = maxImages - currentCount;

    if (availableSlots <= 0) {
      alert('Maximum 5 images allowed. Remove some to add more.');
      return;
    }

    const filesToProcess = Math.min(files.length, availableSlots);

    for (let i = 0; i < filesToProcess; i++) {
      const file = files[i];
      if (file.size > 10 * 1024 * 1024) {
        alert(`Image "${file.name}" is too large. Please use images under 10MB.`);
        continue;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setUploadedImages(prev => {
          if (prev.length >= maxImages) return prev;
          return [...prev, base64];
        });
      };
      reader.readAsDataURL(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllImages = () => {
    setUploadedImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Extract position data from screenshots using Gemini Vision
  const extractPositionsFromImages = async (images: string[]): Promise<any> => {
    setIsExtractingPositions(true);
    try {
      const response = await fetch('/api/vision-extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to extract position data');
      }

      return await response.json();
    } finally {
      setIsExtractingPositions(false);
    }
  };

  // Format extracted positions into a message for Moonshot
  const formatExtractedPositions = (extractedData: any, userPrompt: string): string => {
    if (!extractedData.success || extractedData.positions.length === 0) {
      return `[SCREENSHOT ANALYSIS]

${userPrompt}

Note: Could not extract specific position data from the screenshots. ${extractedData.notes || ''} ${extractedData.errors?.join(', ') || ''}

Please analyze based on what you can see and provide trade management guidance.`;
    }

    const positions = extractedData.positions;
    const positionDetails = positions.map((p: any, i: number) => {
      const pnlSign = (p.pnl || 0) >= 0 ? '+' : '';
      return `Position ${i + 1}: ${p.ticker || 'Unknown'} $${p.strike || '?'} ${p.type?.toUpperCase() || '?'} (${p.expiry || 'Unknown expiry'})
  - Contracts: ${p.contracts || '?'}
  - Avg Cost: $${p.avgCost?.toFixed(2) || '?'} → Current: $${p.currentPrice?.toFixed(2) || '?'}
  - P/L: ${pnlSign}$${p.pnl?.toFixed(0) || '?'} (${pnlSign}${p.pnlPercent?.toFixed(1) || '?'}%)
  - Stock Price: $${p.stockPrice?.toFixed(2) || '?'}
  - Direction: ${p.direction || 'long'}`;
    }).join('\n\n');

    const accountSection = extractedData.account?.accountValue
      ? `\nAccount Info:
  - Account Value: $${extractedData.account.accountValue.toLocaleString()}
  - Buying Power: $${extractedData.account.buyingPower?.toLocaleString() || 'N/A'}
  - Day Change: ${(extractedData.account.dayChange || 0) >= 0 ? '+' : ''}$${extractedData.account.dayChange?.toLocaleString() || '0'}`
      : '';

    return `[SCREENSHOT ANALYSIS - EXTRACTED POSITION DATA]

${userPrompt}

Positions from ${extractedData.imageCount} screenshot(s):

${positionDetails}
${accountSection}

${extractedData.notes ? `Additional Notes: ${extractedData.notes}` : ''}
${extractedData.errors?.length ? `Warnings: ${extractedData.errors.join(', ')}` : ''}

Please analyze these positions using the 7-Step Framework and provide specific trade management recommendations:
1. Should I hold, trim, or close each position?
2. What are the optimal exit targets and stop losses?
3. How should I manage risk across these positions?
4. Any position-specific warnings or opportunities?`;
  };

  const sendMessage = async (overrideMessage?: string) => {
    const messageText = overrideMessage || inputValue;
    if (!messageText.trim() && uploadedImages.length === 0) return;

    // Store images for display before clearing
    const imagesToSend = [...uploadedImages];

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      image: imagesToSend[0], // Show first image in message bubble
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setUploadedImages([]);
    setIsLoading(true);

    try {
      let finalMessage = messageText;

      // If screenshots were uploaded, extract position data first
      if (imagesToSend.length > 0) {
        try {
          const extractedData = await extractPositionsFromImages(imagesToSend);
          finalMessage = formatExtractedPositions(extractedData, messageText || 'Please analyze my positions and give me trade management recommendations.');
        } catch (extractError) {
          console.error('Position extraction error:', extractError);
          // Fall back to basic message if extraction fails
          finalMessage = `[User uploaded ${imagesToSend.length} screenshot(s) of their trading positions]

${messageText || 'Please analyze my positions and give me trade management recommendations.'}

Note: Automatic position extraction encountered an issue. Please provide general trade management guidance based on the 7-Step Framework.`;
        }
      }

      const response = await fetch('/api/moonshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: finalMessage,
          history: messages.map(m => ({ role: m.role, content: m.content }))
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.debug || 'Failed to get response');
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response || 'Sorry, I encountered an error processing your request.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save both messages to database
      saveMessages([
        { role: 'user', content: messageText || '[Screenshot analysis request]' },
        { role: 'assistant', content: assistantMessage.content }
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Sorry, I encountered an error. ${error instanceof Error ? error.message : 'Please try again in a moment.'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleExampleClick = (prompt: string) => {
    if (scannerContext && (
      prompt.includes('recommended trade') ||
      prompt.includes('manage my trade') ||
      prompt.includes('profits or cut losses')
    )) {
      const fullMessage = formatScannerContextMessage(scannerContext, prompt);
      sendMessage(fullMessage);
    } else {
      sendMessage(prompt);
    }
  };

  const scannerPrompts = scannerContext ? [
    {
      icon: <Target size={18} />,
      title: `Analyse my ${scannerContext.ticker} trade`,
      prompt: `Analyse my current recommended trade on ${scannerContext.ticker}. Walk me through the setup using the 7-Step Framework and tell me if this is a good entry.`
    },
    {
      icon: <Shield size={18} />,
      title: "Help me manage my trade",
      prompt: `Help me manage my trade on ${scannerContext.ticker}. What should my profit targets, stop losses, and adjustment triggers be? Give me a clear IF-THEN decision matrix.`
    },
    {
      icon: <TrendingUp size={18} />,
      title: "Take profits & cut losses",
      prompt: `How can I take profits or cut losses in a smart way to manage my balance and risk effectively on my ${scannerContext.ticker} position?`
    }
  ] : [];

  const generalPrompts = [
    {
      icon: <TrendingUp size={18} />,
      title: "Weekly S&P 500 Top Picks",
      prompt: "Search for the top 10 S&P 500 options trading opportunities this week. Focus on liquid, large-cap names with upcoming catalysts, elevated IV rank, unusual options activity, or strong technical setups. For each pick, estimate a score using the 8-Step Viability Framework (Sentiment, Technicals, Options Liquidity, Risk, Greeks, Entry, Review, Sizing). Present results in a ranked table with: Ticker, Estimated Score (/100), Direction (Bullish/Bearish), Suggested Strategy, Key Levels (support/resistance), and Catalyst. Only include stocks that would realistically score 70+ (TRADE). End with a disclaimer: This is hypothetical scenario analysis based on real-time web research, not financial advice. Always do your own due diligence before trading.",
      featured: true
    },
    {
      icon: <Target size={18} />,
      title: "Analyse my position",
      prompt: "I have 5 contracts of AAPL $180 calls expiring in 3 weeks. I bought at $4.50, now worth $6.20. My account is $15k. Should I hold or take profits?"
    },
    {
      icon: <Search size={18} />,
      title: "Find opportunities",
      prompt: "Search the market for options trading opportunities that could be profitable in the next 30-45 days. Look for undervalued setups with good risk/reward using the 7-Step Framework."
    },
    {
      icon: <AlertTriangle size={18} />,
      title: "Managing a losing trade",
      prompt: "My TSLA $250 puts are down 60% with 10 DTE. Stock keeps rallying. When do I cut this trade?"
    }
  ];

  const examplePrompts = scannerContext ? scannerPrompts : generalPrompts;

  const FCA_DISCLAIMER = "Options and derivatives involve significant risk and can result in the loss of your entire investment. Past performance is not indicative of future results. This tool provides educational analysis only and does not constitute financial advice. Alpha Options is not authorised or regulated by the Financial Conduct Authority (FCA). Always consult a qualified financial adviser before making investment decisions.";
  const AI_DISCLAIMER = "Moonshot can make mistakes with analysis, so ensure you do your due diligence and double-check it.";

  return (
    <SignedIn>
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
        {/* Header */}
        <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-base"
              >
                <ArrowLeft size={20} />
                <span className="hidden sm:inline">Back</span>
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                <Rocket size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Ask Moonshot</h1>
                <p className="text-sm text-slate-300">
                  {scannerContext
                    ? `${scannerContext.ticker} @ $${scannerContext.currentPrice.toFixed(2)}`
                    : '7-Step Trade Management AI'
                  }
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={startNewChat}
                className="p-3 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                title="New chat"
              >
                <Plus size={22} />
              </button>
              <button
                onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadConversations(); }}
                className={`p-3 rounded-lg transition-colors ${showHistory ? 'text-purple-400 bg-purple-500/10' : 'text-slate-300 hover:text-white hover:bg-slate-800'}`}
                title="Chat history"
              >
                <History size={22} />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex max-w-5xl mx-auto w-full">

          {/* History Sidebar - Mobile Overlay / Desktop Sidebar */}
          {showHistory && (
            <>
              {/* Mobile backdrop */}
              <div
                className="fixed inset-0 bg-black/60 z-40 md:hidden"
                onClick={() => setShowHistory(false)}
              />
              {/* Sidebar drawer */}
              <div className="fixed inset-y-0 left-0 w-[85vw] max-w-xs z-50 md:relative md:inset-auto md:w-72 md:z-auto border-r border-slate-800 bg-slate-950 md:bg-slate-950/50 overflow-y-auto flex-shrink-0">
                <div className="p-4 md:p-3">
                  <div className="flex items-center justify-between mb-4 md:mb-3">
                    <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wide">Chat History</h3>
                    <button
                      onClick={() => setShowHistory(false)}
                      className="p-2 text-slate-400 hover:text-white md:hidden"
                    >
                      <X size={20} />
                    </button>
                  </div>
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-slate-300" />
                  </div>
                ) : conversations.length === 0 ? (
                  <p className="text-sm text-slate-300 text-center py-8">No conversations yet</p>
                ) : (
                  <div className="space-y-1">
                    {conversations.map((convo) => (
                      <div
                        key={convo.id}
                        onClick={() => loadConversation(convo.id)}
                        className={`group flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                          conversationId === convo.id
                            ? 'bg-purple-500/20 border border-purple-500/30'
                            : 'hover:bg-slate-800/50'
                        }`}
                      >
                        <MessageSquare size={16} className="text-slate-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{convo.title}</div>
                          <div className="text-xs text-slate-300">
                            {convo.message_count} messages &middot; {new Date(convo.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={(e) => deleteConversation(convo.id, e)}
                          className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-2 text-slate-400 hover:text-red-400 transition-all -mr-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* Chat Area */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="p-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl mb-6">
                    <Rocket size={48} className="text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {scannerContext
                      ? `Moonshot ready for ${scannerContext.ticker}`
                      : 'Welcome to MoonshotAI'
                    }
                  </h2>
                  <p className="text-base text-slate-300 max-w-md mb-8">
                    {scannerContext
                      ? `Your 7-Step Analysis is loaded. Choose an option below or type your own question about this ${scannerContext.direction.toLowerCase()} ${scannerContext.strategyName} trade.`
                      : 'Upload a screenshot of your position or describe your trade. I\'ll analyse it using the 7-Step Framework and give you a clear action plan.'
                    }
                  </p>

                  <div className="grid gap-3 w-full max-w-lg">
                    {examplePrompts.map((example: any, i: number) => (
                      <button
                        key={i}
                        onClick={() => handleExampleClick(example.prompt)}
                        className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-colors ${
                          example.featured
                            ? 'bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border-cyan-500/30 hover:border-cyan-500/50'
                            : 'bg-slate-800/50 hover:bg-slate-800 border-slate-700/50'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${example.featured ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-700 text-purple-400'}`}>
                          {example.icon}
                        </div>
                        <div>
                          <div className="text-base font-bold text-white flex items-center gap-2">
                            {example.title}
                            {example.featured && <span className="text-xs bg-gradient-to-r from-cyan-500/30 to-emerald-500/30 text-cyan-300 px-1.5 py-0.5 rounded">NEW</span>}
                          </div>
                          <div className="text-sm text-slate-300 line-clamp-2">{example.featured ? 'Get AI-powered weekly picks from the S&P 500 using the 8-Step Framework' : example.prompt}</div>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="mt-8 space-y-3 max-w-md">
                    <div className="p-3 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                      <p className="text-sm text-slate-300 leading-relaxed text-center">
                        {AI_DISCLAIMER}
                      </p>
                    </div>
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                      <div className="flex items-center gap-2 text-amber-400 text-sm font-bold mb-2">
                        <AlertTriangle size={16} />
                        Important Risk Warning
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        {FCA_DISCLAIMER}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] rounded-2xl p-5 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white'
                          : 'bg-white text-gray-900 shadow-lg border border-gray-200'
                      }`}>
                        {message.image && (
                          <img
                            src={message.image}
                            alt="Uploaded"
                            className="max-w-full rounded-lg mb-3 border border-white/10"
                          />
                        )}
                        {message.role === 'assistant' ? (
                          <div className="moonshot-markdown text-base leading-relaxed">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap text-base leading-relaxed">
                            {message.content}
                          </div>
                        )}
                        <div className={`text-xs mt-3 ${
                          message.role === 'user' ? 'text-purple-200' : 'text-gray-400'
                        }`}>
                          {message.timestamp.toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(isLoading || isExtractingPositions) && (
                    <div className="flex justify-start">
                      <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-200">
                        <div className="flex items-center gap-2 text-gray-500">
                          <Loader2 size={18} className="animate-spin" />
                          <span className="text-base">
                            {isExtractingPositions
                              ? 'Reading your screenshots...'
                              : 'Analysing with 7-Step Framework...'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Image Preview */}
            {uploadedImages.length > 0 && (
              <div className="px-4 py-2 border-t border-slate-800 bg-slate-900/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-slate-400">
                    {uploadedImages.length} screenshot{uploadedImages.length > 1 ? 's' : ''} ({5 - uploadedImages.length} remaining)
                  </span>
                  {uploadedImages.length > 1 && (
                    <button
                      onClick={clearAllImages}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {uploadedImages.map((img, index) => (
                    <div key={index} className="relative inline-block">
                      <img
                        src={img}
                        alt={`Preview ${index + 1}`}
                        className="h-16 rounded-lg border border-slate-600"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-slate-800 bg-slate-950/80">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-end gap-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-300 hover:text-white transition-colors border border-slate-700"
                    title="Upload screenshots (up to 5)"
                  >
                    <ImageIcon size={20} />
                  </button>

                  <div className="flex-1 relative">
                    <textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Describe your position or ask about a trade..."
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 resize-none text-base"
                      rows={1}
                      style={{ minHeight: '48px', maxHeight: '120px' }}
                    />
                  </div>

                  <button
                    onClick={() => sendMessage()}
                    disabled={isLoading || isExtractingPositions || (!inputValue.trim() && uploadedImages.length === 0)}
                    className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white transition-all"
                  >
                    {isLoading || isExtractingPositions ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                  </button>
                </div>

                <p className="text-xs text-slate-300 text-center mt-3 leading-relaxed">
                  {AI_DISCLAIMER}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SignedIn>
  );
};

export default MoonshotPage;
